import { tv } from '@joyful/utils'
import type { ComponentProps } from 'react'
import type { VariantProps } from '@joyful/utils'

import { Slot } from './Slot'

export const boxVariants = tv({
  base: 'min-w-0',

  defaultVariants: {
    hideAbove: 'none',
    marginX: 'none',
    maxWidth: 'none',
    minWidth: 'none',
    padding: 'none',
    width: 'auto',
  },

  variants: {
    height: {
      full: 'h-full',
    },
    /** Needs an ancestor `@container`. */
    hideAbove: {
      md: '@md:hidden',
      none: null,
    },

    marginX: {
      auto: 'mx-auto',
      none: null,
      pull: '-mx-6',
    },

    maxWidth: {
      '3xl': 'max-w-3xl',
      full: 'max-w-full',
      lg: 'max-w-4xl',
      md: 'max-w-64',
      none: null,
      prose: 'max-w-prose',
      sm: 'max-w-40',
      xl: 'max-w-5xl',
    },

    minWidth: {
      content: 'min-w-max',
      md: 'min-w-64',
      none: null,
      sm: 'min-w-40',
    },

    padding: {
      '2xl': 'p-12',
      lg: 'p-6',
      md: 'p-4',
      none: null,
      sm: 'p-2',
      xl: 'p-8',
      xs: 'p-1',
    },

    paddingX: {
      '2xl': 'px-12',
      lg: 'px-6',
      md: 'px-4',
      none: null,
      sm: 'px-2',
      xl: 'px-8',
      xs: 'px-1',
    },

    paddingY: {
      '2xl': 'py-12',
      lg: 'py-6',
      md: 'py-4',
      none: null,
      sm: 'py-2',
      xl: 'py-8',
      xs: 'py-1',
    },

    width: {
      auto: null,
      fit: 'w-fit',
      full: 'w-full',
      var: 'w-(--box-width)',
    },
  },
})

export interface BoxProps
  extends
    Omit<ComponentProps<'div'>, 'className'>,
    VariantProps<typeof boxVariants> {
  /**
   * @default false
   */
  asChild?: boolean
}

/** Non-flex containment: width, height, padding, and centering without surface chrome. */
export function Box({
  asChild = false,
  height,
  hideAbove,
  marginX,
  maxWidth,
  minWidth,
  padding,
  paddingX,
  paddingY,
  width,
  ...restProps
}: BoxProps) {
  const Comp = asChild ? Slot : 'div'

  return (
    <Comp
      className={boxVariants({
        height,
        hideAbove,
        marginX,
        maxWidth,
        minWidth,
        padding,
        paddingX,
        paddingY,
        width,
      })}
      {...restProps}
    />
  )
}
